import { Autocomplete, Button, InputAdornment, InputLabel, Select, SelectChangeEvent, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { FormControl } from "@mui/material";
import { EstimateTreeDto, UpdateEstimateTreeDto, WorkItemType } from "../../../shared/dtos/estimate-tree.dto";
import { generateMenuItemsFromBreakpoints } from "../../../shared/misc/mui-utils";
import { ChangeEvent, useCallback } from "react";
import { SPECIES_LIST } from "./tree-species";
import { isStumpWorkOf } from "../record/work-plan";
import {
  EMPTY_MATERIAL_SPEC, MaterialSpecDto, WorkPlanCatalogDto, WorkPlanQuestionDto, WorkPlanSectionDto,
  workPlanClearedBy, workPlanOptionLabel, workPlanOptions, workPlanQuestionApplies, workPlanSectionsFor,
} from "../../../shared/dtos/work-plan-catalog.dto";

export interface WorkItemSpecificFieldsProps {
  savedTree: EstimateTreeDto,
  updatedTree: UpdateEstimateTreeDto,
  setUpdatedTree: React.Dispatch<React.SetStateAction<UpdateEstimateTreeDto>>,
  canEdit: boolean,
  /** Library preparation (SQ-500): exact inputs, blanks stay unknown, work plan answers, inferred hints. */
  exact?: ExactFieldsOptions,
}

export interface ExactFieldsOptions {
  operationTypes: string[];
  inferred?: InferredFacts | null;
  /** The shared Work Plan catalog; without it the four original questions are offered. */
  workPlan?: WorkPlanCatalogDto | null;
  /** A compact loader is on the work item's equipment, which brings in the loader questions. */
  hasLoader?: boolean;
}

export interface InferredFacts {
  species?: string | null;
  heightFt?: number | null;
  widthFt?: number | null;
  trunkDiameterIn?: number | null;
}

interface UpdatedTreeGetAndSet {
  updatedTree: UpdateEstimateTreeDto,
  setUpdatedTree: React.Dispatch<React.SetStateAction<UpdateEstimateTreeDto>>,
  canEdit: boolean
}

// Shared fields
const TreeHeightField = ({label, updatedTree, setUpdatedTree, canEdit}: { label: string } & UpdatedTreeGetAndSet) => {
  const treeHeightBreakpoints = [...Array.from(Array(501).keys())]
  const handleTreeHeightChange = (event: SelectChangeEvent) => {
    setUpdatedTree(tree => ({...tree!, height: event.target.value}));
  };

  return (      
    <FormControl fullWidth>
      <InputLabel required={true} id={`${label}-height-select-label`}>{label}</InputLabel>
      <Select
        disabled={!canEdit}
        labelId={`${label}-height`}
        id={`${label}-height-select`}
        value={updatedTree.height ?? '0 Feet'}
        label={label}
        onChange={handleTreeHeightChange}
      >
        {generateMenuItemsFromBreakpoints(treeHeightBreakpoints, 'Feet')}
      </Select>
    </FormControl>)
}

const TreeWidthField = ({label, updatedTree, setUpdatedTree, canEdit}: { label: string } & UpdatedTreeGetAndSet) => {
  const treeWidthBreakpoints = [...Array.from(Array(501).keys())]
  const handleTreeWidthChange = (event: SelectChangeEvent) => {
    setUpdatedTree(tree => ({...tree!, width: event.target.value}));
  };

  return (      
    <FormControl fullWidth>
      <InputLabel required={true} id="tree-width-select-label">{label}</InputLabel>
      <Select
        disabled={!canEdit}
        labelId={`${label}-width`}
        id={`${label}-width-select`}
        value={updatedTree.width ?? '0 Feet'}
        label={label}
        onChange={handleTreeWidthChange}
      >
        {generateMenuItemsFromBreakpoints(treeWidthBreakpoints, 'Feet')}
      </Select>
    </FormControl>)
}

const TreeTrunkDiameterField = ({label, updatedTree, setUpdatedTree, canEdit}: { label: string } & UpdatedTreeGetAndSet) => {
  const trunkDiameterBreakpoints = [...Array.from(Array(501).keys())]
  const handleTreeTrunkDiameterChange = (event: SelectChangeEvent) => {
    setUpdatedTree(tree => ({...tree!, trunkDiameter: event.target.value}));
  };

  return (
    <FormControl fullWidth>
      <InputLabel required={true} id="tree-trunk-diameter-select-label">{label}</InputLabel>
      <Select
        disabled={!canEdit}
        labelId={`${label}-trunk-diameter`}
        id={`${label}-trunk-diameter-select`}
        value={updatedTree.trunkDiameter ?? '0 Inches'}
        label={label}
        onChange={handleTreeTrunkDiameterChange}
      >
        {generateMenuItemsFromBreakpoints(trunkDiameterBreakpoints, 'Inches')}
      </Select>
    </FormControl>)
}

const TreeSpeciesField = ({label, updatedTree, setUpdatedTree, canEdit}: { label: string } & UpdatedTreeGetAndSet) => {
  const handleTreeSpeciesChange = (value: string) => {
    setUpdatedTree(tree => ({...tree!, species: value}));
  };

  return (
    <FormControl fullWidth>
      <Autocomplete
        disabled={!canEdit}
        disablePortal
        freeSolo
        options={SPECIES_LIST}
        value={updatedTree.species ?? ''}
        onInputChange={(_, v) => handleTreeSpeciesChange(v)}
        renderInput={(params) => <TextField required={false} {...params} label={label}/>}
      />
    </FormControl>)
}

// Android/mockup parity (AC-267): every work item carries a Debris Management
// note, stored in the tree's additionalDetails (there is no dedicated column).
const DebrisManagementField = ({updatedTree, setUpdatedTree, canEdit}: UpdatedTreeGetAndSet) => {
  const handleDebrisManagementChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setUpdatedTree(tree => ({...tree!, additionalDetails: {...tree.additionalDetails, debrisManagement: event.target.value}}));
  }, [setUpdatedTree]);

  return (
    <TextField
      disabled={!canEdit}
      label={'Debris Management'}
      variant="outlined"
      value={updatedTree?.additionalDetails?.debrisManagement ?? ''}
      onChange={handleDebrisManagementChange}
    />)
}

const NotesField = ({label, updatedTree, setUpdatedTree, canEdit}: { label: string } & UpdatedTreeGetAndSet) => {
  const handleNotesChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setUpdatedTree(tree => ({...tree!, notes: event.target.value}));
  }, [setUpdatedTree]);

  return (
    <TextField
      disabled={!canEdit}
      label={label}
      variant="outlined"
      multiline
      inputProps={{autoCapitalize: 'sentences'}}
      rows={2}
      value={updatedTree?.notes ?? ''}
      onChange={handleNotesChange}
    />)
}

// Tree Group specific fields
const NumberOfTreesInGroupField = ({updatedTree, setUpdatedTree, canEdit}: UpdatedTreeGetAndSet) => {
  const treeCountBreakpoints = [...Array.from(Array(99).keys())].map(x => x + 1)
  const handleNumberOfTreesInGroupChange = (event: SelectChangeEvent<string | number>) => {
    setUpdatedTree(tree => ({...tree!, additionalDetails: {...tree.additionalDetails, TreeGroup_treeCount: event.target.value}}));
  };

  return (
    <FormControl key={'tree-group-tree-count'} fullWidth>
      <InputLabel>Number of Trees in Group *</InputLabel>
      <Select
        disabled={!canEdit}
        label={'Number of Trees in Group'}
        value={updatedTree.additionalDetails?.TreeGroup_treeCount ?? '1 '}
        onChange={handleNumberOfTreesInGroupChange}
      >
        {generateMenuItemsFromBreakpoints(treeCountBreakpoints, '')}
      </Select>
    </FormControl>)
}

// Debris Pile specific fields
const DebrisPileLengthField = ({label, updatedTree, setUpdatedTree, canEdit}: { label: string } & UpdatedTreeGetAndSet) => {
  const debrisPileLengthBreakpoints = [...Array.from(Array(100).keys())]
  const handleDebrisPileLengthChange = (event: SelectChangeEvent) => {
    setUpdatedTree(tree => ({...tree!, additionalDetails: {...tree.additionalDetails, DebrisPile_length: event.target.value}}));
  };

  return (      
    <FormControl fullWidth>
      <InputLabel required={true} id="debris-pile-length-select-label">{label}</InputLabel>
      <Select
        disabled={!canEdit}
        labelId={`${label}-length`}
        id={`${label}-length-select`}
        value={updatedTree.additionalDetails?.DebrisPile_length?.toString() ?? '0 Feet'}
        label={label}
        onChange={handleDebrisPileLengthChange}
      >
        {generateMenuItemsFromBreakpoints(debrisPileLengthBreakpoints, 'Feet')}
      </Select>
    </FormControl>)
}

export const EstimateTreeDetailDataFields = ({savedTree, updatedTree, setUpdatedTree, canEdit, exact}: WorkItemSpecificFieldsProps) => {
  if (exact) {
    return <ExactDataFields savedTree={savedTree} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit} exact={exact}/>;
  }

  const treeFields = 
    <>
      <TreeHeightField label={'Tree Height (ft)'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <TreeWidthField label={'Tree Width (ft)'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <TreeTrunkDiameterField label={'Tree Trunk Diameter (in)'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <TreeSpeciesField label={'Tree Species'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <DebrisManagementField updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <NotesField label={'Tree Notes'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
    </>

  const TreeGroupFields =
    <>
      <NumberOfTreesInGroupField updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <TreeHeightField label={'Average Tree Height (ft)'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <TreeWidthField label={'Average Tree Width (ft)'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <TreeTrunkDiameterField label={'Average Tree Trunk Diameter (in)'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <TreeSpeciesField label={'Tree Species'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <DebrisManagementField updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <NotesField label={'Tree Group Notes'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
    </>

  const FallenTreeFields =
    <>
      <TreeHeightField label={'Tree Height (ft)'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <TreeWidthField label={'Tree Width (ft)'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <TreeTrunkDiameterField label={'Tree Trunk Diameter (in)'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <TreeSpeciesField label={'Tree Species'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <DebrisManagementField updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <NotesField label={'Tree Notes'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
    </>

  const StumpFields =
    <>
      <TreeSpeciesField label={'Stump Species'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <DebrisManagementField updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <NotesField label={'Stump Notes'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
    </>

  const DebrisPileFields =
    <>
      <TreeHeightField label={'Debris Pile Height (ft)'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <TreeWidthField label={'Debris Pile Width (ft)'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <DebrisPileLengthField label={'Debris Pile Length (ft)'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <TreeSpeciesField label={'Debris Pile Species'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <DebrisManagementField updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <NotesField label={'Debris Pile Notes'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
    </>

  // The task's description is the Misc Task's Task Details; the separate "Task Descriptive Note" this used to offer
  // moved to the operations' notes in V83 (SQ-671), and a Misc has the same notes field as every other kind.
  const MiscFields =
    <>
      <DebrisManagementField updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
      <NotesField label={'Misc Notes'} updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
    </>

  switch (savedTree.workItemType) {
    case WorkItemType.Tree:
      return treeFields
    case WorkItemType.TreeGroup:
      return TreeGroupFields
    case WorkItemType.FallenTree:
      return FallenTreeFields
    case WorkItemType.Stump:
      return StumpFields
    case WorkItemType.DebrisPile:
      return DebrisPileFields
    case WorkItemType.Misc:
      return MiscFields
    default:
      return <></>
  }
}

// --- Library preparation (SQ-500) -------------------------------------------
// Exact values with units instead of 0–500 menus, and no silent defaults: a
// blank is unknown and is saved as unknown, never as zero.

type Unit = 'Feet' | 'Inches';
const NUMBER_OR_RANGE = /^\d{1,4}(\.\d{1,2})?(\s*-\s*\d{1,4}(\.\d{1,2})?)?$/;

const unitLabel: Record<Unit, string> = {Feet: 'ft', Inches: 'in'};

/** "67 Feet" → "67"; anything else stays as typed so nothing is lost. */
const stripUnit = (value: string | number | undefined | null) =>
  value === undefined || value === null ? '' : String(value).replace(/\s*(feet|foot|ft|inches|inch|in)\s*$/i, '');

export const exactValueIsValid = (value: string | number | undefined | null) => {
  const text = stripUnit(value).trim();
  return text === '' || NUMBER_OR_RANGE.test(text);
};

const withUnit = (text: string, unit: Unit) => {
  const trimmed = text.trim().replace(/\s*-\s*/, '-');
  return trimmed === '' ? undefined : `${trimmed} ${unit}`;
};

const InferredHint = ({text, onAccept, canEdit}: {text: string, onAccept: () => void, canEdit: boolean}) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{mt: 0.5}}>
    <Typography variant="caption" color="text.secondary">Inferred from photos: {text} · not yet reviewed</Typography>
    {canEdit && <Button size="small" onClick={onAccept} sx={{minWidth: 0, py: 0}}>Accept</Button>}
  </Stack>
);

const ExactNumberField = ({label, unit, value, onChange, canEdit, inferred, testId}: {
  label: string, unit: Unit, value: string | number | undefined | null, onChange: (value: string | undefined) => void,
  canEdit: boolean, inferred?: number | null, testId: string,
}) => {
  const text = stripUnit(value);
  const valid = exactValueIsValid(value);
  return (
    <div style={{flex: 1, minWidth: 0}}>
      <TextField
        fullWidth
        disabled={!canEdit}
        label={label}
        value={text}
        placeholder="Unknown"
        inputProps={{inputMode: 'decimal', 'data-testid': testId}}
        InputProps={{endAdornment: <InputAdornment position="end">{unitLabel[unit]}</InputAdornment>}}
        error={!valid}
        helperText={!valid ? 'Enter a number (or a range like 30-40), or leave blank if unknown' : undefined}
        onChange={event => onChange(withUnit(event.target.value, unit))}
      />
      {text === '' && inferred !== undefined && inferred !== null &&
        <InferredHint text={`${inferred} ${unitLabel[unit]}`} canEdit={canEdit} onAccept={() => onChange(`${inferred} ${unit}`)}/>}
    </div>
  );
};

const ExactCountField = ({label, value, onChange, canEdit, testId}: {
  label: string, value: string | number | undefined | null, onChange: (value: string | undefined) => void, canEdit: boolean, testId: string,
}) => {
  const text = value === undefined || value === null ? '' : String(value).trim();
  const valid = text === '' || /^\d{1,4}$/.test(text);
  return (
    <TextField
      fullWidth
      sx={{flex: 1, minWidth: 0}}
      disabled={!canEdit}
      label={label}
      value={text}
      placeholder="Unknown"
      inputProps={{inputMode: 'numeric', 'data-testid': testId}}
      error={!valid}
      helperText={!valid ? 'Enter a whole number, or leave blank if unknown' : undefined}
      onChange={event => onChange(event.target.value.trim() === '' ? undefined : event.target.value.trim())}
    />
  );
};

// Until the shared catalog has loaded: Android's four original WorkPlan questions (WorkPlan.kt).
// "No Rigging" is SQ-500's explicit answer beside Android's three amounts.
const TREE_WORK = ['Tree Removal', 'Thinning', 'Custom Trim/Prune', 'Ground to Canopy Clearance', 'Crown Reduction'];
const FALLBACK_WORK_PLAN: WorkPlanCatalogDto = {
  version: 0,
  keys: ['removalStrategy', 'reachMethod', 'debrisMethod', 'riggingAmount'],
  sections: [
    {id: 'removalPlan', title: 'Removal Plan', operations: ['Tree Removal'], questions: [
      {key: 'removalStrategy', title: 'Removal plan', type: 'choice', options: ['Fell Whole Tree', 'Removed Piece by Piece']}]},
    {id: 'treeWork', title: 'Tree Work', operations: TREE_WORK, questions: [
      {key: 'reachMethod', title: 'Reach method', type: 'choice', options: ['Climbing', 'Done from the Ground']},
      {key: 'debrisMethod', title: 'How debris comes down', type: 'choice', options: ['Freely Fall', 'Carefully Dropped']},
      {key: 'riggingAmount', title: 'Rigging', type: 'choice', options: ['A Little', 'Quite a Bit', 'A Lot']}]},
  ],
  materialSpec: {fields: [], lengthMode: ['Any Length', 'Cut to Length'], location: ['Where It Falls', 'Specific Location']},
  rules: {webOnlyOptions: {riggingAmount: ['No Rigging']}},
};

type AdditionalKey = keyof NonNullable<UpdateEstimateTreeDto['additionalDetails']>;
type Answers = Record<string, unknown>;

const MaterialSpecFields = ({catalog, question, value, onChange, canEdit}: {
  catalog: WorkPlanCatalogDto; question: WorkPlanQuestionDto; value: MaterialSpecDto; onChange: (spec: MaterialSpecDto | undefined) => void; canEdit: boolean;
}) => {
  const set = (patch: Partial<MaterialSpecDto>) => onChange({...value, ...patch});
  const int = (text: string) => (text.trim() === '' ? 0 : Math.max(0, Math.floor(Number(text)) || 0));
  return (
    <Stack spacing={1} sx={{pl: 1, borderLeft: 2, borderColor: 'divider'}} data-review-field={`work-plan-${question.key}`}>
      <Typography variant="subtitle2">{question.title}</Typography>
      <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}>
        <TextField size="small" label="Diameter and bigger (in)" value={value.diameter || ''} disabled={!canEdit} sx={{width: 170}}
                   inputProps={{inputMode: 'numeric'}} onChange={event => set({diameter: int(event.target.value)})}/>
        <ToggleButtonGroup exclusive size="small" disabled={!canEdit} value={value.lengthMode || null}
                           onChange={(_, mode: string | null) => set({lengthMode: mode ?? ''})}>
          {catalog.materialSpec.lengthMode.map(option => <ToggleButton key={option} value={option} sx={{textTransform: 'none'}}>{option}</ToggleButton>)}
        </ToggleButtonGroup>
      </Stack>
      {value.lengthMode === 'Cut to Length' && (
        <Stack direction="row" spacing={1}>
          <TextField size="small" label="ft" value={value.lengthFt || ''} disabled={!canEdit} sx={{width: 90}} inputProps={{inputMode: 'numeric'}}
                     onChange={event => set({lengthFt: int(event.target.value)})}/>
          <TextField size="small" label="in" value={value.lengthIn || ''} disabled={!canEdit} sx={{width: 90}} inputProps={{inputMode: 'numeric'}}
                     onChange={event => set({lengthIn: int(event.target.value)})}/>
        </Stack>
      )}
      <ToggleButtonGroup exclusive size="small" disabled={!canEdit} value={value.location || null}
                         onChange={(_, location: string | null) => set({location: location ?? ''})}>
        {catalog.materialSpec.location.map(option => <ToggleButton key={option} value={option} sx={{textTransform: 'none'}}>{option}</ToggleButton>)}
      </ToggleButtonGroup>
      {value.location === 'Specific Location' && (
        <TextField size="small" label="Where" value={value.locationNote} disabled={!canEdit} inputProps={{maxLength: 200, autoCapitalize: 'sentences'}}
                   onChange={event => set({locationNote: event.target.value})}/>
      )}
      <Button size="small" disabled={!canEdit} sx={{alignSelf: 'flex-start'}} onClick={() => onChange(undefined)}>Clear</Button>
    </Stack>
  );
};

/**
 * The Work Plan questions a work item's operations bring in, from the shared
 * catalog: chips for a choice, a number, a note, or a material spec. An answer
 * is stored as its chip label under the question's key; clearing removes the
 * key, and an answer that clears others (Fell Whole Tree) removes theirs too.
 */
const WorkPlanQuestions = ({catalog, operationTypes, hasLoader, isStump, answers, setAnswer, canEdit}: {
  catalog: WorkPlanCatalogDto; operationTypes: string[]; hasLoader: boolean; isStump: boolean; answers: Answers;
  setAnswer: (patch: Record<string, unknown>) => void; canEdit: boolean;
}) => {
  // Any Misc Task makes it a Misc work item, as on the record page, Android and iOS (a Misc with two tasks is one too).
  const isMisc = operationTypes.includes('Misc Task');
  const sections: WorkPlanSectionDto[] = workPlanSectionsFor(catalog, operationTypes, hasLoader);
  const answer = (question: WorkPlanQuestionDto, value: unknown) => {
    const patch: Record<string, unknown> = {[question.key]: value};
    workPlanClearedBy(catalog, question.key, typeof value === 'string' ? value : undefined).forEach(key => { patch[key] = undefined; });
    // A follow-up's answer goes with the answer it followed.
    sections.flatMap(section => section.questions).forEach(follow => {
      const accepted = follow.followsAnswers?.[question.key];
      if (accepted && !accepted.includes(String(value ?? ''))) patch[follow.key] = undefined;
    });
    // A stump answer this change narrowed out goes (the loader's Grindings once Cleanup leaves the grindings,
    // SQ-644): offered before the change and not after. An answer the old one did not offer either (a Brush / Wood
    // answer saved on a stump before SQ-644) is not this change's to clear, and stays, as on the record page.
    const next = {...answers, ...patch};
    sections.flatMap(section => section.questions).forEach(other => {
      const held = next[other.key];
      if (isStump && question.key in (other.stumpOptionsByAnswer ?? {}) && typeof held === 'string' && held !== ''
          && workPlanOptions(catalog, other, isMisc, isStump, answers).includes(held)
          && !workPlanOptions(catalog, other, isMisc, isStump, next).includes(held)) patch[other.key] = undefined;
    });
    setAnswer(patch);
  };
  return (
    <>
      {sections.map(section => (
        <Stack key={section.id} spacing={1.5}>
          <Typography variant="subtitle1" fontWeight={600}>{isMisc && section.miscTitle ? section.miscTitle : section.title}</Typography>
          {section.questions.filter(question => workPlanQuestionApplies(question, answers)).map(question => {
            const value = answers[question.key];
            // A follow-up's title can depend on the answer it follows ("Where The Excess Goes").
            const followed = Object.keys(question.followsAnswers ?? {})[0];
            const title = (followed && question.titleByAnswer?.[String(answers[followed] ?? '')]) || question.title;
            if (question.type === 'materialSpec') {
              return <MaterialSpecFields key={question.key} catalog={catalog} question={question} canEdit={canEdit}
                                         value={{...EMPTY_MATERIAL_SPEC, ...(typeof value === 'object' && value ? value as Partial<MaterialSpecDto> : {})}}
                                         onChange={spec => answer(question, spec)}/>;
            }
            if (question.type === 'integer') {
              return <TextField key={question.key} size="small" label={title} value={value ?? ''} disabled={!canEdit} sx={{maxWidth: 260}}
                                inputProps={{inputMode: 'numeric', 'data-testid': `work-plan-${question.key}`}} placeholder="Unknown"
                                onChange={event => answer(question, event.target.value.trim() === '' ? undefined : Math.max(0, Math.floor(Number(event.target.value)) || 0))}/>;
            }
            if (question.type === 'text') {
              return <TextField key={question.key} size="small" label={title} value={value ?? ''} disabled={!canEdit}
                                inputProps={{maxLength: 200, autoCapitalize: 'sentences', 'data-testid': `work-plan-${question.key}`}} placeholder="Unknown"
                                onChange={event => answer(question, event.target.value === '' ? undefined : event.target.value)}/>;
            }
            return (
              <div key={question.key}>
                <Typography variant="subtitle2" id={`work-plan-${question.key}`} sx={{mb: 0.5}}>{title}</Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  disabled={!canEdit}
                  aria-labelledby={`work-plan-${question.key}`}
                  data-testid={`work-plan-${question.key}`}
                  value={typeof value === 'string' && value !== '' ? value : null}
                  onChange={(_, picked: string | null) => answer(question, picked ?? undefined)}
                  sx={{flexWrap: 'wrap'}}
                >
                  {workPlanOptions(catalog, question, isMisc, isStump, answers).map(option =>
                    <ToggleButton key={option} value={option} sx={{textTransform: 'none'}}>{workPlanOptionLabel(question, option, isStump, answers)}</ToggleButton>)}
                </ToggleButtonGroup>
                {(value === undefined || value === '') && <Typography variant="caption" color="text.secondary" sx={{ml: 1}}>Unknown</Typography>}
              </div>
            );
          })}
        </Stack>
      ))}
    </>
  );
};

const ExactDataFields = ({savedTree, updatedTree, setUpdatedTree, canEdit, exact}: WorkItemSpecificFieldsProps & {exact: ExactFieldsOptions}) => {
  const type = savedTree.workItemType;
  const set = (field: 'height' | 'width' | 'trunkDiameter' | 'species') => (value: string | undefined) =>
    setUpdatedTree(tree => ({...tree, [field]: value}));
  const setDetail = (key: AdditionalKey) => (value: string | undefined) =>
    setUpdatedTree(tree => ({...tree, additionalDetails: {...tree.additionalDetails, [key]: value}}));
  const details = updatedTree.additionalDetails ?? {};
  const inferred = exact.inferred;
  const sized = type === WorkItemType.Tree || type === WorkItemType.TreeGroup || type === WorkItemType.FallenTree || type === WorkItemType.DebrisPile;
  const trunked = type === WorkItemType.Tree || type === WorkItemType.TreeGroup || type === WorkItemType.FallenTree || type === WorkItemType.Stump;
  const averaged = type === WorkItemType.TreeGroup ? 'Average ' : '';
  const catalog = exact.workPlan ?? FALLBACK_WORK_PLAN;
  const setAnswers = (patch: Record<string, unknown>) => setUpdatedTree(tree => {
    const next = {...(tree.additionalDetails ?? {})} as Record<string, unknown>;
    Object.entries(patch).forEach(([key, value]) => {
      if (value === undefined) delete next[key];
      else next[key] = value;
    });
    return {...tree, additionalDetails: next as UpdateEstimateTreeDto['additionalDetails']};
  });
  return (
    <Stack spacing={2}>
      {type !== WorkItemType.Misc && (
        <div>
          <FormControl fullWidth>
            <Autocomplete
              disabled={!canEdit}
              freeSolo
              options={SPECIES_LIST}
              value={updatedTree.species ?? ''}
              onInputChange={(_, v) => set('species')(v.trim() === '' ? undefined : v)}
              // The value changes when staff move between items; float the label whenever there is one.
              renderInput={(params) => <TextField {...params} label="Species" placeholder="Unknown"
                InputLabelProps={{...params.InputLabelProps, shrink: updatedTree.species ? true : undefined}}
                inputProps={{...params.inputProps, 'data-testid': 'fact-species'}}/>}
            />
          </FormControl>
          {!updatedTree.species && inferred?.species &&
            <InferredHint text={inferred.species} canEdit={canEdit} onAccept={() => set('species')(inferred.species!)}/>}
        </div>
      )}
      {type === WorkItemType.TreeGroup &&
        <ExactCountField label="Trees in group" value={details.TreeGroup_treeCount} onChange={setDetail('TreeGroup_treeCount')} canEdit={canEdit} testId="fact-tree-count"/>}
      {sized && (
        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
          <ExactNumberField label={`${averaged}Height`} unit="Feet" value={updatedTree.height} onChange={set('height')} canEdit={canEdit} inferred={inferred?.heightFt} testId="fact-height"/>
          <ExactNumberField label={`${averaged}Width`} unit="Feet" value={updatedTree.width} onChange={set('width')} canEdit={canEdit} inferred={inferred?.widthFt} testId="fact-width"/>
          {type === WorkItemType.DebrisPile &&
            <ExactNumberField label="Length" unit="Feet" value={details.DebrisPile_length} onChange={setDetail('DebrisPile_length')} canEdit={canEdit} testId="fact-length"/>}
        </Stack>
      )}
      {trunked && (
        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
          {/* A stump's size lives in the trunk column (Android calls it Stump Diameter too); it is the stump's, not a tree's. */}
          <ExactNumberField label={type === WorkItemType.Stump ? 'Stump diameter' : `${averaged}Trunk diameter`} unit="Inches" value={updatedTree.trunkDiameter} onChange={set('trunkDiameter')} canEdit={canEdit} inferred={inferred?.trunkDiameterIn} testId="fact-trunk"/>
          {(type === WorkItemType.Tree || type === WorkItemType.TreeGroup || type === WorkItemType.FallenTree) &&
            <ExactCountField label={type === WorkItemType.TreeGroup ? 'Average trunks per tree' : 'Trunks'} value={details.Tree_trunkCount} onChange={setDetail('Tree_trunkCount')} canEdit={canEdit} testId="fact-trunk-count"/>}
        </Stack>
      )}
      <WorkPlanQuestions catalog={catalog} operationTypes={exact.operationTypes} hasLoader={!!exact.hasLoader}
                         isStump={isStumpWorkOf(type, exact.operationTypes)} answers={details as Answers} setAnswer={setAnswers} canEdit={canEdit}/>
      {/* Misc: the library reads the operation's Task Details, so the tree-level description is not offered here. */}
      <DebrisManagementField updatedTree={updatedTree} setUpdatedTree={setUpdatedTree} canEdit={canEdit}/>
    </Stack>
  );
};
